import React, {
  useState,
  forwardRef,
  useEffect,
  useCallback,
  useRef,
  useImperativeHandle,
} from "react";
import { useLocation } from "react-router-dom";
import {
  Table,
  Input,
  Button,
  Select,
  Upload,
  Modal,
  Form,
  message,
  Card,
  Space,
  Typography,
  Divider,
} from "antd";
import { UploadOutlined, PlusCircleOutlined, DeleteOutlined } from "@ant-design/icons";
import Swal from "sweetalert2";
import {
  getMarketingFilesByOfferId,
  getMarketingFileTypes,
  createMarketingFileType,
  getMarketingFilesNotLinkedToMarketing,
  createMarketingFile,
  deleteMarketingFile,
  cloneMarketingFile,
} from "../../core/api/marketing";
import { useSelector } from "react-redux";

const EnvelopesSection = forwardRef((props, ref) => {
  const { code_offer, brand_id } = props;
  const location = useLocation();
  const parts = location.pathname.split("/");
  const urlMarketingId = parts[3] ? Number(parts[3]) : null;

  const [fileCode, setFileCode] = useState(code_offer);
  const [loading, setLoading] = useState(false);
  const [offerId, setOfferId] = useState(urlMarketingId);

  const [marketingFiles, setMarketingFiles] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [newFileType, setNewFileType] = useState("");
  const [newFileTypeCode, setNewFileTypeCode] = useState("");
  const [fileTypes, setFileTypes] = useState([]);
  const [existingFiles, setExistingFiles] = useState([]);
  const selectedClients = useSelector((state) => state.selectedClients);

  const fetchMarketingFiles = useCallback(
    async (marketingId) => {
      try {
        const response = await getMarketingFilesByOfferId(marketingId);
        setMarketingFiles(
          response.data.length
            ? response.data.map((file) => ({
                key: file.id,
                fileType: file.type?.name || "",
                selectFromExisting: "No",
                fileName: file.file_name || "",
                pages: file.number_of_pages || "",
                length: file.length || "",
                width: file.width || "",
                file: file.absolute_url || null,
                id: file.id,
              }))
            : []
        );
      } catch (error) {
        message.error("Failed to fetch marketing files.");
      }
    },
    [fileCode]
  );

  const fetchFileTypes = useCallback(async () => {
    try {
      const response = await getMarketingFileTypes();
      setFileTypes(
        response.data.map((type) => ({
          value: type.name,
          code: type.code,
          id: type.id,
        }))
      );
    } catch (error) {
      message.error("Failed to fetch file types.");
    }
  }, []);

  const fetchExistingFiles = useCallback(
    async (typeId = null) => {
      try {
        const response = await getMarketingFilesNotLinkedToMarketing(typeId, selectedClients);
        setExistingFiles(
          response.data.map((file) => ({
            label: file.file_name,
            value: file.id,
            pages: file.number_of_pages,
            length: file.length,
            width: file.width,
            file: file.absolute_url,
            type_id: file.type?.id,
            type_name: file.type?.name,
          }))
        );
      } catch (error) {
        message.error("Failed to fetch existing files.");
      }
    },
    [selectedClients]
  );
  const addFileType = () => {
    setIsModalVisible(true);
  };

  const handleModalOk = async () => {
    if (newFileType.trim()) {
      try {
        const response = await createMarketingFileType({
          name: newFileType,
          code: newFileTypeCode,
        });
        setFileTypes([
          ...fileTypes,
          {
            value: response.data.name,
            code: response.data.code,
            id: response.data.id,
          },
        ]);
        message.success("File type added successfully.");
        setIsModalVisible(false);
      } catch (error) {
        message.error("Failed to add file type.");
      }
    }
  };

  const handleModalCancel = () => {
    setNewFileType("");
    setNewFileTypeCode("");
    setIsModalVisible(false);
  };

  const updateMarketingFile = (key, field, value) => {
    setMarketingFiles((prevFiles) =>
      prevFiles.map((file, index) => {
        if (file.key === key) {
          const updatedFile = { ...file, [field]: value };

          if (field === "fileType" && value) {
            const selectedFileType = fileTypes.find((type) => type.value === value);
            const fileTypeCode = selectedFileType?.code || "";
            const fileTypeId = selectedFileType?.id;

            fetchExistingFiles(fileTypeId);
            updatedFile.fileName = `${fileCode}-${fileTypeCode}-${index + 1}`;
          }

          if (field === "fileName" && file.selectFromExisting === "Yes") {
            const selectedFile = existingFiles.find((existingFile) => existingFile.value === value);

            if (selectedFile) {
              updatedFile.pages = selectedFile.pages;
              updatedFile.length = selectedFile.length;
              updatedFile.width = selectedFile.width;
              updatedFile.file = selectedFile.file;
            }
          }

          return updatedFile;
        }
        return file;
      })
    );
  };

  const addMarketingFile = () => {
    const newFile = {
      key: Date.now().toString(),
      fileType: "",
      selectFromExisting: "No",
      fileName: "",
      pages: "",
      length: "",
      width: "",
      file: null,
    };
    setMarketingFiles([...marketingFiles, newFile]);
  };

  const saveMarketingFiles = async (newCodeOffer, offer_id) => {
    setOfferId(offer_id);
    if (loading) return;
    setLoading(true);

    // Use the passed code offer if available, otherwise use the local state
    if (newCodeOffer && newCodeOffer !== fileCode) {
      setFileCode(newCodeOffer);
    }

    // Update any file names that use the code offer pattern
    const updatedFiles = marketingFiles.map((file) => {
      // If the file name doesn't already include the code offer, add it
      if (file.fileName && !file.fileName.includes(newCodeOffer || fileCode)) {
        return {
          ...file,
          fileName: `${newCodeOffer || fileCode}-${file.fileName}`,
        };
      } else if (file.fileName && file.fileName.startsWith(fileCode) && newCodeOffer) {
        // If the file name already includes the old code offer, replace it
        return {
          ...file,
          fileName: file.fileName.replace(fileCode, newCodeOffer),
        };
      }
      return file;
    });

    // Always use the updated files for processing
    setMarketingFiles(updatedFiles);

    setTimeout(async () => {
      try {
        // Use the updated files array instead of the state which might not be updated yet
        for (const file of updatedFiles) {
          const typeId = fileTypes.find((type) => type.value === file.fileType)?.id;
          if (
            !file.fileType ||
            !file.fileName ||
            !file.pages ||
            !file.length ||
            !file.width ||
            (!file.file && file.selectFromExisting === "No")
          ) {
            Swal.fire({
              icon: "error",
              title: "Validation Error",
              text: "All fields are required for each marketing file.",
            });
            return;
          }

          if (file.selectFromExisting === "Yes") {
            const selectedFileId = file.fileName;

            if (selectedFileId) {
              try {
                const cloneData = {
                  source_file_id: selectedFileId,
                  type_id: typeId,
                  file_code: fileCode,
                  offer: offer_id,
                  linked_to_marketing: true,
                  brand: brand_id,
                };

                await cloneMarketingFile(cloneData);
              } catch (error) {
                console.error("Error cloning file:", error);
                throw error;
              }
            }
          } else {
            // Ensure file_name includes the offer code
            let fileName = file.fileName;
            const codeToUse = newCodeOffer || fileCode;

            // If fileName doesn't start with the code offer, prepend it
            if (!fileName.startsWith(codeToUse)) {
              fileName = `${codeToUse}-${fileName}`;
            }

            const fileData = {
              type_id: typeId,
              file_code: codeToUse,
              file_name: fileName,
              offer: offer_id || urlMarketingId, // Prioritize passed offer_id over urlMarketingId
              number_of_pages: file.pages,
              length: file.length,
              width: file.width,
              linked_to_marketing: true,
              brand: brand_id,
            };
            if (file.file && typeof file.file !== "string") {
              // If file is an Ant Design Upload file object, get the originFileObj
              if (file.file.originFileObj) {
                fileData.file = file.file.originFileObj;
              } else if (file.file.file) {
                // Handle case where file might be nested
                fileData.file = file.file.file;
              } else {
                fileData.file = file.file;
              }
            }
            if (file.id) {
              await updateMarketingFile(file.id, fileData);
            } else {
              await createMarketingFile(fileData);
            }
          }
        }

        message.success("Marketing files saved successfully.");
        fetchMarketingFiles(offer_id);
      } catch (error) {
        console.error("Error saving marketing files:", error);
        message.error("Failed to save marketing files.");
      } finally {
        setLoading(false);
      }
    }, 1000);
  };

  useImperativeHandle(ref, () => ({
    saveMarketingAllFiles(newCodeOffer, offer_id) {
      return saveMarketingFiles(newCodeOffer, offer_id);
    },
  }));

  const deleteFile = (record) => {
    Modal.confirm({
      title: "Are you sure you want to delete this file?",
      content: "This action cannot be undone.",
      okText: "Yes, Delete",
      okType: "danger",
      cancelText: "No",
      onOk: async () => {
        if (record.id) {
          try {
            await deleteMarketingFile(record.id);
            fetchMarketingFiles();
            message.success("File deleted successfully.");
          } catch (error) {
            message.error("Failed to delete file.");
          }
        } else {
          setMarketingFiles(marketingFiles.filter((file) => file.key !== record.key));
          message.success("Unsaved file removed.");
        }
      },
    });
  };

  // Add useEffect after all function definitions
  useEffect(() => {
    setOfferId(urlMarketingId);

    if (urlMarketingId) {
      fetchMarketingFiles(urlMarketingId);
      fetchFileTypes();
      fetchExistingFiles();
    } else {
      // Clear marketing files data when there's no offer_id
      fetchFileTypes();
      setMarketingFiles([]);
      setExistingFiles([]);
    }
  }, [urlMarketingId, fetchMarketingFiles, fetchFileTypes, fetchExistingFiles]);

  const marketingFileColumns = [
    {
      title: (
        <Space>
          <span>File Type</span>
        </Space>
      ),
      dataIndex: "fileType",
      key: "fileType",
      width: 180,
      fixed: "left",
      ellipsis: true,
      render: (_, record) => (
        <Select
          value={record.fileType}
          onChange={(value, id) => updateMarketingFile(record.key, "fileType", value, id)}
          options={fileTypes.map((type) => ({
            key: type.id,
            label: type.value,
            value: type.value,
          }))}
          placeholder="Select Type"
          style={{ minWidth: 150 }}
          allowClear
        />
      ),
    },

    {
      title: "Select from Existing",
      dataIndex: "selectFromExisting",
      key: "selectFromExisting",
      width: 100,
      render: (_, record) => (
        <Select
          value={record.selectFromExisting}
          onChange={(value) => updateMarketingFile(record.key, "selectFromExisting", value)}
          options={[
            { value: "No", label: "No" },
            { value: "Yes", label: "Yes" },
          ]}
        />
      ),
    },
    {
      title: "File Name",
      dataIndex: "fileName",
      key: "fileName",
      width: 200,
      render: (_, record) =>
        record.selectFromExisting === "Yes" ? (
          <Select
            value={record.fileName}
            onChange={(value) => updateMarketingFile(record.key, "fileName", value)}
            style={{ minWidth: 200 }}
            options={
              record.fileType
                ? existingFiles
                    .filter((file) => {
                      const typeId = fileTypes.find((type) => type.value === record.fileType)?.id;
                      return file.type_id === typeId;
                    })
                    .map((file) => ({
                      label: file.label,
                      value: file.value,
                    }))
                : []
            }
            placeholder="Select Existing File"
          />
        ) : (
          <Input
            value={record.fileName}
            onChange={(e) => updateMarketingFile(record.key, "fileName", e.target.value)}
            placeholder="Enter File Name"
          />
        ),
    },
    {
      title: "Pages",
      dataIndex: "pages",
      key: "pages",
      width: 100,
      render: (_, record) => (
        <Input
          placeholder="Enter pages"
          value={record.pages}
          onChange={(e) => updateMarketingFile(record.key, "pages", e.target.value)}
          readOnly={record.selectFromExisting === "Yes"}
        />
      ),
    },
    {
      title: "Length",
      dataIndex: "length",
      key: "length",
      width: 100,
      render: (_, record) => (
        <Input
          placeholder="Enter length"
          value={record.length}
          onChange={(e) => updateMarketingFile(record.key, "length", e.target.value)}
          readOnly={record.selectFromExisting === "Yes"}
        />
      ),
    },
    {
      title: "Width",
      dataIndex: "width",
      key: "width",
      width: 100,
      render: (_, record) => (
        <Input
          placeholder="Enter width"
          value={record.width}
          onChange={(e) => updateMarketingFile(record.key, "width", e.target.value)}
          readOnly={record.selectFromExisting === "Yes"}
        />
      ),
    },
    {
      title: "File",
      dataIndex: "file",
      key: "file",
      width: 130,
      render: (_, record) =>
        record.file ? (
          <a
            href={typeof record.file === "string" ? record.file : URL.createObjectURL(record.file)}
            target="_blank"
            rel="noopener noreferrer">
            Preview File
          </a>
        ) : (
          <Upload
            beforeUpload={(file) => {
              updateMarketingFile(record.key, "file", file);
              return false;
            }}
            maxCount={1}
            accept=".pdf">
            <Button icon={<UploadOutlined />}>Upload PDF</Button>
          </Upload>
        ),
    },

    {
      title: "Action",
      key: "action",
      width: 150,
      fixed: "right",
      render: (_, record) => (
        <Button
          type="link"
          danger
          icon={<DeleteOutlined />}
          onClick={() => deleteFile(record)}
          title="Delete"
        />
      ),
    },
  ];

  return (
    <div>
      <Card style={{ marginTop: 16 }}>
        <Typography.Title level={5}>Marketing Files</Typography.Title>
        <div
          style={{
            marginBottom: 16,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}>
          <div style={{ maxWidth: 400 }}>
            <Typography.Text>File Code</Typography.Text>
            <Input value={fileCode || code_offer} readOnly title="File Code" name="file-code" />
          </div>
          <Button type="primary" size="small" onClick={addFileType} icon={<PlusCircleOutlined />}>
            Add New File Type
          </Button>
        </div>
        <Table
          dataSource={marketingFiles}
          columns={marketingFileColumns}
          pagination={false}
          rowKey="key"
          scroll={{ x: "max-content" }}
          bordered
          size="middle"
          className="marketing-files-table"
          style={{
            "--antd-wave-shadow-color": "transparent",
            "--scroll-bar-size": "12px",
          }}
        />

        {/* Add CSS to fix the overlapping headers */}
        <style>{`
          .marketing-files-table .ant-table-cell {
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }
          
          .marketing-files-table .ant-table-cell-fix-left,
          .marketing-files-table .ant-table-cell-fix-right {
            z-index: 2;
          }
          
          .marketing-files-table .ant-table-thead .ant-table-cell-fix-left,
          .marketing-files-table .ant-table-thead .ant-table-cell-fix-right {
            z-index: 3;
            background-color: #f0f0f0;
          }
        `}</style>

        <Divider />
        <Space style={{ display: "flex", justifyContent: "space-between" }}>
          <Button type="default" onClick={addMarketingFile} icon={<PlusCircleOutlined />}>
            Add More
          </Button>
        </Space>
      </Card>
      <Modal
        title="Add File Type"
        open={isModalVisible}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
        zIndex={2000}
        centered>
        <Form>
          <Form.Item label="File Type">
            <Input
              value={newFileType}
              onChange={(e) => setNewFileType(e.target.value)}
              placeholder="Enter file type name"
            />
          </Form.Item>
          <Form.Item label="File Code">
            <Input
              value={newFileTypeCode}
              onChange={(e) => setNewFileTypeCode(e.target.value)}
              placeholder="File type code"
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
});

EnvelopesSection.displayName = "EnvelopesSection";

export default EnvelopesSection;
